<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { database } from '@/firebase/config'
import { ref as dbRef, get, off } from 'firebase/database'
import { ICertificate, IDegree } from '@shared/types'
import BaseModal from '../BaseModal.vue'
interface TPackage {
	id: string
	type: 'certificates' | 'degrees'
	zipBase64: string
	label?: string
	date?: number
	responsables?: {
		[key: string]: any
	}
	sentData?: {
		cargaTituloElectronico: {
			mensaje: string
			numeroLote: string
		}
		descargaTituloElectronico: {
			mensaje: string
			numeroLote: string
			titulosBase64: string
		}
		consultaProcesoTituloElectronico: {
			mensaje: string
			numeroLote: string
			estatusLote: number
		}
	}
}

const store = useStore()
const db = database

//ESTO si
const certificatesList = ref<ICertificate[]>([])
const certificatesListTestMode = ref<ICertificate[]>([])
const degrees = ref<IDegree[]>([])
const degreesTestMode = ref<IDegree[]>([])
const packages = ref<TPackage[]>([])
const packagesTestMode = ref<TPackage[]>([])
const currentInstitution = computed(() => store.getters['global/currentInstitution'])
const loading = ref(false)
const showErrorModal = ref(false)
const errorMessage = ref<string>('')

// Firebase paths
const getInstitutionPathTestMode = (path: string) => `/institutions/${currentInstitution.value}/testMode${path}`
const getInstitutionPath = (path: string) => `/institutions/${currentInstitution.value}${path}`

const loadPackagesCertificates = async () => {
	// Clear previous listeners
	const packagesRef = dbRef(db, getInstitutionPath('/certificates'))
	off(packagesRef)

	return new Promise<void>((resolve, reject) => {
		get(packagesRef)
			.then((snapshot: any) => {
				const data = snapshot.val()
				if (data) {
					const queuePackages = data.packages
					const packagesCertificates = getPackagesCertificatesFiltered(queuePackages, false)
					getCertificates(packagesCertificates, false)
				} else {
					packages.value = []
				}
				resolve()
			})
			.catch((error: any) => {
				console.error('Error loading packages:', error)
				packages.value = []
				reject(error)
			})
	})
}
const loadPackagesCertificatesTestMode = async () => {
	// Clear previous listeners
	const packagesRef = dbRef(db, getInstitutionPathTestMode('/certificates'))
	off(packagesRef)

	return new Promise<void>((resolve, reject) => {
		get(packagesRef)
			.then((snapshot: any) => {
				const data = snapshot.val()
				if (data) {
					const queuePackages = data.packages
					const packagesCertificates = getPackagesCertificatesFiltered(queuePackages, true)
					getCertificates(packagesCertificates, true)
				} else {
					packagesTestMode.value = []
				}
				resolve()
			})
			.catch((error: any) => {
				console.error('Error loading packages:', error)
				packagesTestMode.value = []
				reject(error)
			})
	})
}
const loadPackagesDegrees = async () => {
	// Clear previous listeners
	const packagesRef = dbRef(db, getInstitutionPath('/degrees'))
	off(packagesRef)

	return new Promise<void>((resolve, reject) => {
		get(packagesRef)
			.then((snapshot: any) => {
				const data = snapshot.val()
				if (data) {
					const queuePackages = data.packages
					const packagesDegrees = getPackagesFiltered(queuePackages, false)
					getDegrees(packagesDegrees, false)
				} else {
					packages.value = []
				}
				resolve()
			})
			.catch((error: any) => {
				console.error('Error loading packages:', error)
				packages.value = []
				reject(error)
			})
	})
}
const loadPackagesDegreesTestMode = async () => {
	// Clear previous listeners
	const packagesRef = dbRef(db, getInstitutionPathTestMode('/degrees'))
	off(packagesRef)

	return new Promise<void>((resolve, reject) => {
		get(packagesRef)
			.then((snapshot: any) => {
				const data = snapshot.val()
				if (data) {
					const queuePackages = data.packages
					const packagesDegrees = getPackagesFiltered(queuePackages, true)
					getDegrees(packagesDegrees, true)
				} else {
					packagesTestMode.value = []
				}
				resolve()
			})
			.catch((error: any) => {
				console.error('Error loading packages:', error)
				packagesTestMode.value = []
				reject(error)
			})
	})
}
const getPackagesFiltered = (queuePackages: TPackage[], isTestMode: boolean) => {
	const packagesDegrees = Object.entries(queuePackages)
		.map(([id, pkg]: [string, any]) => ({
			id,
			...pkg
		}))
		.filter((pkg: any) => pkg.sign === true)
	packagesDegrees.forEach((pkg: any) => {
		if (isTestMode) {
			packagesTestMode.value.push(pkg)
		} else {
			packages.value.push(pkg)
		}
	})
	return packagesDegrees
}
const getPackagesCertificatesFiltered = (queuePackages: TPackage[], isTestMode: boolean) => {
	const packagesCertificates = Object.entries(queuePackages)
		.map(([id, pkg]: [string, any]) => ({
			id,
			...pkg
		}))
		.filter((pkg: any) => pkg.sign === true)
	packagesCertificates.forEach((pkg: any) => {
		if (isTestMode) {
			packagesTestMode.value.push(pkg)
		} else {
			packages.value.push(pkg)
		}
	})
	return packagesCertificates
}
const getCertificates = (packagesCertificates: any[], isTestMode: boolean) => {
	for (const pkg of packagesCertificates) {
		const certificatesPackages: Record<string, boolean> = pkg?.certificates || {}
		for (const certificate in certificatesPackages) {
			// Limpiar listeners anteriores
			let certificatesRef: any
			if (isTestMode) {
				certificatesRef = dbRef(db, getInstitutionPathTestMode(`/certificates/queue/${certificate}`))
			} else {
				certificatesRef = dbRef(db, getInstitutionPath(`/certificates/queue/${certificate}`))
			}
			off(certificatesRef)
			// Cargar datos iniciales y luego configurar listener continu
			get(certificatesRef).then((snapshot: any) => {
				if (snapshot.exists()) {
					if (isTestMode) {
						certificatesListTestMode.value.push({
							id: snapshot.key,
							...snapshot.val()
						})
					} else {
						certificatesList.value.push({
							id: snapshot.key,
							...snapshot.val()
						})
					}
				}
			})
		}
	}
}
const getDegrees = (packagesDegrees: any[], isTestMode: boolean) => {
	for (const pkg of packagesDegrees) {
		const degreesPackages: Record<string, boolean> = pkg?.degrees || {}
		for (const degree in degreesPackages) {
			// Limpiar listeners anteriores
			let degreesRef: any
			if (isTestMode) {
				degreesRef = dbRef(db, getInstitutionPathTestMode(`/degrees/queue/${degree}`))
			} else {
				degreesRef = dbRef(db, getInstitutionPath(`/degrees/queue/${degree}`))
			}
			off(degreesRef)
			// Cargar datos iniciales y luego configurar listener continu
			get(degreesRef).then((snapshot: any) => {
				if (snapshot.exists()) {
					if (isTestMode) {
						degreesTestMode.value.push({
							id: snapshot.key,
							...snapshot.val()
						})
					} else {
						degrees.value.push({
							id: snapshot.key,
							...snapshot.val()
						})
					}
				}
			})
		}
	}
}
// Watch for institution ID changes
watch(currentInstitution, (newId, oldId) => {
	if (newId !== oldId) {
		certificatesList.value = []
		certificatesListTestMode.value = []
		degrees.value = []
		degreesTestMode.value = []
		packages.value = []
		packagesTestMode.value = []
		loading.value = true
		Promise.all([loadPackagesCertificates(), loadPackagesCertificatesTestMode(), loadPackagesDegrees(), loadPackagesDegreesTestMode()]).finally(() => {
			loading.value = false
		})
	}
})

// Initialize
onMounted(async () => {
	// Make sure we have the institution ID before loading certificates
	if (!currentInstitution.value) {
		try {
			// Try to load institutions if not already loaded
			await store.dispatch('global/loadInstitutions', null, { root: true })

			// Check again if we have an institution ID
			if (!currentInstitution.value) {
				errorMessage.value = 'No se pudo determinar la institución. Por favor, seleccione una institución.'
				showErrorModal.value = true
				return
			}
		} catch (error) {
			console.error('Error loading institutions:', error)
			errorMessage.value = 'Error al cargar las instituciones. Por favor, intente nuevamente.'
			showErrorModal.value = true
			return
		}
	}

	loading.value = true
	Promise.all([loadPackagesCertificates(), loadPackagesCertificatesTestMode(), loadPackagesDegrees(), loadPackagesDegreesTestMode()]).finally(() => {
		loading.value = false
	})

	// Cleanup function
	return () => {
		// Limpiar listeners al desmontar el componente
		const certificatesRef = dbRef(db, getInstitutionPath('/certificates'))
		const packagesRef = dbRef(db, getInstitutionPath('/certificates'))
		off(certificatesRef)
		off(packagesRef)
	}
})
const getFirmantes = (responsables: any) => Object.keys(responsables).join(', ')

const generateTemplate = async () => {
	const XLSX = await import('xlsx')

	// Encabezados del Excel
	const headers = ['Folio Control', 'Tipo de documento', 'Emitido en', 'CURP', 'Institución', 'Fecha']
	const rows: any[][] = [headers]

	// Función auxiliar para obtener CURPs de responsables
	const getCurpsFromResponsables = (responsables: any): string => {
		if (!responsables || typeof responsables !== 'object') return ''
		return Object.keys(responsables).join(', ')
	}

	// Función auxiliar para formatear fecha
	const formatDate = (date: number | string | undefined): string => {
		if (!date) return ''
		const dateObj = new Date(date)
		return dateObj.toLocaleDateString('es-MX', {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit'
		})
	}

	// Agregar certificados de producción
	for (const certificate of certificatesList.value) {
		const curp = getCurpsFromResponsables(certificate.data?.FirmaResponsables)
		const institucion = certificate.data?.Ipes?.nombreInstitucion || 'Sin institución'
		const fecha = formatDate(certificate.date)
		rows.push([certificate.folioControl || '', 'Certificado', 'Producción', curp, institucion, fecha])
	}

	// Agregar certificados de prueba
	for (const certificate of certificatesListTestMode.value) {
		const curp = getCurpsFromResponsables(certificate.data?.FirmaResponsables)
		const institucion = certificate.data?.Ipes?.nombreInstitucion || 'Sin institución'
		const fecha = formatDate(certificate.date)
		rows.push([certificate.folioControl || '', 'Certificado', 'Prueba', curp, institucion, fecha])
	}

	// Agregar títulos de producción
	for (const degree of degrees.value) {
		const curp = getCurpsFromResponsables(degree.data?.FirmaResponsables)
		const institucion = degree.data?.Institucion?.nombreInstitucion || 'Sin institución'
		const fecha = formatDate(degree.date)
		rows.push([degree.folioControl || '', 'Título', 'Producción', curp, institucion, fecha])
	}

	// Agregar títulos de prueba
	for (const degree of degreesTestMode.value) {
		const curp = getCurpsFromResponsables(degree.data?.FirmaResponsables)
		const institucion = degree.data?.Institucion?.nombreInstitucion || 'Sin institución'
		const fecha = formatDate(degree.date)
		rows.push([degree.folioControl || '', 'Título', 'Prueba', curp, institucion, fecha])
	}

	// Crear el archivo Excel
	const ws = XLSX.utils.aoa_to_sheet(rows)
	const wb = XLSX.utils.book_new()
	XLSX.utils.book_append_sheet(wb, ws, 'Datos Diarios')

	const secondPage = [
		'Total de certificados emitidos en producción: ',
		'Total de certificados emitidos en prueba: ',
		'Total de títulos emitidos en producción: ',
		'Total de títulos emitidos en prueba: ',
		'Total de paquetes emitidos en producción: ',
		'Total de paquetes emitidos en prueba: '
	]
	const rows2: any[][] = [
		[secondPage[0], certificatesList.value.length],
		[secondPage[1], certificatesListTestMode.value.length],
		[secondPage[2], degrees.value.length],
		[secondPage[3], degreesTestMode.value.length],
		[secondPage[4], packages.value.length],
		[secondPage[5], packagesTestMode.value.length]
	]
	const wsSecondPage = XLSX.utils.aoa_to_sheet(rows2)
	XLSX.utils.book_append_sheet(wb, wsSecondPage, 'Total de documentos emitidos')

	// Generar nombre de archivo con fecha actual
	const fechaActual = new Date().toISOString().split('T')[0]
	XLSX.writeFile(wb, `bitacora_documentos_${fechaActual}.xlsx`)
}
</script>
<template>
	<VaCard square bordered style="padding: 2rem !important">
		<VaCardTitle>
			<div class="row align-items-center">
				<div class="col-md-11 text-center">
					<div class="row">
						<div class="col-md-8 text-left"><p style="font-size: 1.5rem; font-weight: bold">Bitácora de documentos emitidos</p></div>
						<div class="col-md-2">
							<va-button @click="generateTemplate" class="price-button" color="primary" :disabled="loading">
								<va-icon name="update" class="button-icon" />
								Generar reporte
							</va-button>
						</div>
					</div>
				</div>
			</div>
		</VaCardTitle>
		<VaCardContent>
			<div class="package-grid last-section">
				<div class="package-card info-card">
					<div class="card-content-wrapper">
						<div class="info-header">
							<div class="info-icon-wrapper">
								<va-icon name="event" class="info-icon" />
							</div>
							<div class="card-title-group">
								<h3 class="card-title">Certificados emitidos en producción</h3>
								<p class="card-subtitle">Cantidad de certificados emitidos en producción</p>
							</div>
						</div>
						<div class="info-value">
							{{ certificatesList.length }}
						</div>
					</div>
				</div>

				<div class="package-card info-card">
					<div class="card-content-wrapper">
						<div class="info-header">
							<div class="info-icon-wrapper">
								<va-icon name="person" class="info-icon" />
							</div>
							<div class="card-title-group">
								<h3 class="card-title">Certificados emitidos en prueba</h3>
								<p class="card-subtitle">Cantidad de certificados emitidos en prueba</p>
							</div>
						</div>
						<div class="info-value">
							{{ certificatesListTestMode.length }}
						</div>
					</div>
				</div>
			</div>
			<div class="package-grid last-section">
				<div class="package-card info-card">
					<div class="card-content-wrapper">
						<div class="info-header">
							<div class="info-icon-wrapper">
								<va-icon name="event" class="info-icon" />
							</div>
							<div class="card-title-group">
								<h3 class="card-title">Títulos emitidos en producción</h3>
								<p class="card-subtitle">Cantidad de títulos emitidos en producción</p>
							</div>
						</div>
						<div class="info-value">
							{{ degrees.length }}
						</div>
					</div>
				</div>

				<div class="package-card info-card">
					<div class="card-content-wrapper">
						<div class="info-header">
							<div class="info-icon-wrapper">
								<va-icon name="person" class="info-icon" />
							</div>
							<div class="card-title-group">
								<h3 class="card-title">Títulos emitidos en prueba</h3>
								<p class="card-subtitle">Cantidad de títulos emitidos en prueba</p>
							</div>
						</div>
						<div class="info-value">
							{{ degreesTestMode.length }}
						</div>
					</div>
				</div>
			</div>
			<div class="package-grid last-section">
				<div class="package-card info-card">
					<div class="card-content-wrapper">
						<div class="info-header">
							<div class="info-icon-wrapper">
								<va-icon name="event" class="info-icon" />
							</div>
							<div class="card-title-group">
								<h3 class="card-title">Paquetes emitidos en producción</h3>
								<p class="card-subtitle">Cantidad de paquetes emitidos en producción</p>
							</div>
						</div>
						<div class="info-value">
							{{ packages.length }}
						</div>
					</div>
				</div>

				<div class="package-card info-card">
					<div class="card-content-wrapper">
						<div class="info-header">
							<div class="info-icon-wrapper">
								<va-icon name="person" class="info-icon" />
							</div>
							<div class="card-title-group">
								<h3 class="card-title">Paquetes emitidos en prueba</h3>
								<p class="card-subtitle">Cantidad de paquetes emitidos en prueba</p>
							</div>
						</div>
						<div class="info-value">
							{{ packagesTestMode.length }}
						</div>
					</div>
				</div>
			</div>
			<hr class="divider" style="color: #0d6efd" />
			<div class="content">
				<div v-if="loading" class="loading-container">
					<VaProgressCircle indeterminate color="primary" size="large" />
					<p class="loading-text">Cargando documentos...</p>
				</div>
				<div v-else-if="certificatesList.length === 0 && certificatesListTestMode.length === 0 && degrees.length === 0 && degreesTestMode.length === 0" class="empty-state">
					<img src="@/assets/icons/certificateIcon.svg" alt="No certificates" class="empty-icon" />
					<p class="empty-title">Aún no has creado certificados</p>
					<p class="empty-description">Crea un nuevo certificado o importa varios desde un archivo de excel</p>
				</div>
				<div v-else>
					<!-- Sección de Certificados: Producción y Prueba lado a lado -->
					<h3 class="section-title">Certificados</h3>
					<div class="tables-row">
						<div class="table-container">
							<div class="va-table-responsive">
								<table class="certificates-table">
									<thead>
										<tr>
											<th colspan="4" style="text-align: center">Certificados emitidos en producción</th>
										</tr>
										<tr>
											<th>Folio Control</th>
											<th>CURP</th>
											<th>Institución</th>
											<th>Fecha</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="certificate in certificatesList" :key="certificate.id">
											<td>{{ certificate.folioControl }}</td>
											<td>{{ getFirmantes(certificate.data.FirmaResponsables) }}</td>
											<td>{{ certificate.data.Ipes.idNombreInstitucion === 0 ? 'Sin institución' : certificate.data.Ipes.nombreInstitucion }}</td>
											<td>{{ new Date(certificate.date).toLocaleDateString() }}</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
						<div class="table-container">
							<div class="va-table-responsive">
								<table class="certificates-table">
									<thead>
										<tr>
											<th colspan="4" style="text-align: center">Certificados emitidos en prueba</th>
										</tr>
										<tr>
											<th>Folio Control</th>
											<th>CURP</th>
											<th>Institución</th>
											<th>Fecha</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="certificate in certificatesListTestMode" :key="certificate.id">
											<td>{{ certificate.folioControl }}</td>
											<td>{{ getFirmantes(certificate.data.FirmaResponsables) }}</td>
											<td>{{ certificate.data.Ipes.idNombreInstitucion === 0 ? 'Sin institución' : certificate.data.Ipes.nombreInstitucion }}</td>
											<td>{{ new Date(certificate.date).toLocaleDateString() }}</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					</div>

					<!-- Sección de Títulos: Producción y Prueba lado a lado -->
					<h3 class="section-title">Títulos</h3>
					<div class="tables-row">
						<div class="table-container">
							<div class="va-table-responsive">
								<table class="certificates-table">
									<thead>
										<tr>
											<th colspan="4" style="text-align: center">Títulos emitidos en producción</th>
										</tr>
										<tr>
											<th>Folio Control</th>
											<th>CURP</th>
											<th>Institución</th>
											<th>Fecha</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="degree in degrees" :key="degree.id">
											<td>{{ degree.folioControl }}</td>
											<td>{{ getFirmantes(degree.data.FirmaResponsables) }}</td>
											<td>{{ degree.data.Institucion.nombreInstitucion }}</td>
											<td>{{ new Date(degree.date).toLocaleDateString() }}</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
						<div class="table-container">
							<div class="va-table-responsive">
								<table class="certificates-table">
									<thead>
										<tr>
											<th colspan="4" style="text-align: center">Títulos emitidos en prueba</th>
										</tr>
										<tr>
											<th>Folio Control</th>
											<th>CURP</th>
											<th>Institución</th>
											<th>Fecha</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="degree in degreesTestMode" :key="degree.id">
											<td>{{ degree.folioControl }}</td>
											<td>{{ getFirmantes(degree.data.FirmaResponsables) }}</td>
											<td>{{ degree.data.Institucion.nombreInstitucion }}</td>
											<td>{{ new Date(degree.date).toLocaleDateString() }}</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
			</div>
		</VaCardContent>
	</VaCard>
	<BaseModal v-model="showErrorModal" title="Error" :message="errorMessage" :testMode="false" size="medium">
		<template #body>
			<div class="error-message">
				<div class="error-header">
					<div class="error-icon">
						<i class="fas fa-exclamation-triangle"></i>
					</div>
					<h3 class="error-title">Error</h3>
				</div>
				<p class="error-description">{{ errorMessage }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showErrorModal = false">Entendido</button>
		</template>
	</BaseModal>
</template>
<style scoped>
.error-description {
	font-size: 1.1rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
	border-left: 4px solid #f71d1d;
}
.error-message {
	background: linear-gradient(135deg, #ecbaba 0%, #e67a7a 100%);
	border: 2px solid #f71d1d;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 32px rgba(254, 178, 178, 0.3);
	animation: slideInError 0.4s ease-out;
}

.error-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 2px solid #f68484;
}

.error-icon {
	width: 48px;
	height: 48px;
	background: linear-gradient(135deg, #e67a7a 0%, #e67a7a 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 16px rgba(245, 101, 101, 0.4);
}

.error-icon i {
	color: white;
	font-size: 1.5rem;
	animation: pulse 2s infinite;
}

.error-title {
	margin: 0;
	color: #e67a7a;
	font-size: 1.5rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.price-button {
	width: 100%;
	padding: 0.875rem 1.5rem;
	font-size: 1rem;
	font-weight: 600;
	border-radius: 12px;
	box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
	transition: all 0.3s ease;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.5rem;
	text-transform: none;
	letter-spacing: 0.3px;
}

.price-button:hover:not(:disabled) {
	transform: translateY(-2px);
	box-shadow: 0 6px 20px rgba(37, 99, 235, 0.4);
}

.price-button:active:not(:disabled) {
	transform: translateY(0);
}

.price-button:disabled {
	opacity: 0.6;
	cursor: not-allowed;
}
.content {
	background: white;
	border-radius: 8px;
	padding: 20px;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.empty-state {
	text-align: center;
	padding: 40px;
	color: #666;
}

/* Estilos para habilitar scroll en el contenedor principal */
:deep(.main-content) {
	overflow-y: auto !important;
	height: calc(100vh - 60px) !important;
	padding: 1rem !important;
}

/* Asegurar que el contenido tenga scroll */
:deep(.va-card) {
	max-height: none;
	overflow: visible;
}

/* Asegurar que las tablas tengan scroll horizontal si es necesario */
:deep(.va-data-table) {
	overflow-x: auto;
}

/* Estilos para el scroll personalizado */
:deep(.main-content)::-webkit-scrollbar {
	width: 8px;
}

:deep(.main-content)::-webkit-scrollbar-track {
	background: #f1f1f1;
	border-radius: 4px;
}

:deep(.main-content)::-webkit-scrollbar-thumb {
	background: #888;
	border-radius: 4px;
}

:deep(.main-content)::-webkit-scrollbar-thumb:hover {
	background: #555;
}

/* ===== ESTILOS PARA SECCIÓN DE BÚSQUEDA ===== */

.empty-icon {
	font-size: 4rem;
	color: #6c757d;
	margin-bottom: 1rem;
}

.empty-title {
	font-size: 1.5rem;
	font-weight: 600;
	color: #1a2a3a;
	margin-bottom: 0.5rem;
}

.empty-description {
	color: #6c757d;
	font-size: 1rem;
	margin: 0;
}

.loading-container {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 60px 20px;
	gap: 20px;
}

.loading-text {
	font-size: 1.1rem;
	color: #667eea;
	font-weight: 500;
	margin: 0;
}

/* Animaciones */
@keyframes fadeIn {
	from {
		opacity: 0;
		transform: scale(0.8);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

/* Responsive Design para Búsqueda */
@media (max-width: 768px) {
	.empty-icon {
		font-size: 3rem;
	}

	.empty-title {
		font-size: 1.25rem;
	}
}

/* Animaciones */
@keyframes fadeIn {
	from {
		opacity: 0;
		transform: scale(0.8);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

/* ===== ESTILOS PARA TABLA DE CERTIFICADOS ===== */
.section-title {
	font-size: 1.5rem;
	font-weight: 700;
	color: #1a2a3a;
	margin: 30px 0 20px 0;
	padding-bottom: 10px;
	border-bottom: 3px solid #667eea;
}

.tables-row {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 20px;
	margin-bottom: 30px;
}

.table-container {
	max-height: 600px;
	overflow-y: auto;
	overflow-x: auto;
	border-radius: 8px;
	border: 1px solid #e0e0e0;
	background: white;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
	flex: 1;
}

.table-container::-webkit-scrollbar {
	width: 10px;
	height: 10px;
}

.table-container::-webkit-scrollbar-track {
	background: #f5f5f5;
	border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb {
	background: #c1c1c1;
	border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb:hover {
	background: #a8a8a8;
}

.certificates-table {
	width: 100%;
	border-collapse: collapse;
	font-size: 0.95rem;
	background: white;
}

.certificates-table thead {
	position: sticky;
	top: 0;
	z-index: 10;
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
	color: white;
}

.certificates-table thead th {
	padding: 16px 20px;
	text-align: left;
	font-weight: 600;
	font-size: 0.9rem;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	border-bottom: 2px solid rgba(255, 255, 255, 0.2);
	white-space: nowrap;
}

.certificates-table tbody tr {
	border-bottom: 1px solid #e8e8e8;
	transition: all 0.2s ease;
}

.certificates-table tbody tr:hover {
	background-color: #f8f9ff;
	transform: scale(1.01);
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.certificates-table tbody tr:nth-child(even) {
	background-color: #fafafa;
}

.certificates-table tbody tr:nth-child(even):hover {
	background-color: #f8f9ff;
}

.certificates-table tbody td {
	padding: 14px 20px;
	color: #333;
	vertical-align: middle;
}

.certificates-table tbody td:first-child {
	font-weight: 600;
	color: #062342;
}

/* Responsive para tabla */
@media (max-width: 1200px) {
	.tables-row {
		grid-template-columns: 1fr;
		gap: 15px;
	}
}

@media (max-width: 768px) {
	.section-title {
		font-size: 1.25rem;
		margin: 20px 0 15px 0;
	}

	.tables-row {
		grid-template-columns: 1fr;
		gap: 15px;
		margin-bottom: 20px;
	}

	.table-container {
		max-height: 400px;
	}

	.certificates-table {
		font-size: 0.85rem;
	}

	.certificates-table thead th,
	.certificates-table tbody td {
		padding: 10px 12px;
	}
}

/* ====================================================== */
/* ====================== ESTILOS ====================== */
/* ====================================================== */

.last-section {
	margin-bottom: 0;
}
/* Grid de tarjetas */
.package-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
	gap: 1.5rem;
}

/* Tarjetas base */
.package-card {
	position: relative;
	background: white;
	border-radius: 16px;
	padding: 0;
	transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
	overflow: hidden;
	animation: fadeInScale 0.6s ease-out;
	border: 1px solid rgba(0, 0, 0, 0.05);
}

.package-card::before {
	content: '';
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	height: 4px;
	background: linear-gradient(90deg, transparent 0%, rgba(102, 126, 234, 0.5) 50%, transparent 100%);
	opacity: 0;
	transition: opacity 0.3s ease;
}

.package-card:hover {
	transform: translateY(-8px) scale(1.02);
	box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

.package-card:hover::before {
	opacity: 1;
}

.card-content-wrapper {
	position: relative;
	padding: 1.75rem;
	z-index: 1;
}
.card-title-group {
	flex: 1;
}

.card-title {
	font-size: 1.125rem;
	font-weight: 700;
	color: #1a202c;
	margin: 0 0 0.25rem 0;
	letter-spacing: -0.3px;
}

.card-subtitle {
	font-size: 0.875rem;
	color: #718096;
	margin: 0;
	font-weight: 500;
}

.financial-card .card-content-wrapper {
	padding: 2rem;
}

.financial-content .card-title {
	color: white;
}

.financial-content .card-subtitle {
	color: rgba(255, 255, 255, 0.8);
}

/* Tarjetas de información */
.info-card {
	background: white;
}

.info-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
}

.info-icon-wrapper {
	width: 48px;
	height: 48px;
	border-radius: 12px;
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
	display: flex;
	align-items: center;
	justify-content: center;
	transition: all 0.3s ease;
}

.package-card:hover .info-icon-wrapper {
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
	transform: scale(1.1);
}

.info-icon {
	color: #ffffff;
	font-size: 1.5rem;
}

.info-value {
	font-size: 1.25rem;
	font-weight: 700;
	color: #1a202c;
	letter-spacing: -0.3px;
}

/* Animaciones */
@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes fadeInScale {
	from {
		opacity: 0;
		transform: scale(0.9);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

@keyframes slideInLeft {
	from {
		opacity: 0;
		transform: translateX(-20px);
	}
	to {
		opacity: 1;
		transform: translateX(0);
	}
}

@keyframes pulse {
	0%,
	100% {
		transform: scale(1);
	}
	50% {
		transform: scale(1.1);
	}
}

@keyframes shine {
	0% {
		transform: translateX(-100%) translateY(-100%) rotate(45deg);
	}
	100% {
		transform: translateX(100%) translateY(100%) rotate(45deg);
	}
}

@keyframes float {
	0%,
	100% {
		transform: translateY(0);
	}
	50% {
		transform: translateY(-10px);
	}
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

/* Responsive */
@media (max-width: 1024px) {
	.package-grid {
		grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
		gap: 1.25rem;
	}
}

@media (max-width: 768px) {
	.package-grid {
		grid-template-columns: 1fr;
		gap: 1rem;
	}
}

@media (max-width: 480px) {
	.card-content-wrapper {
		padding: 1.25rem;
	}
}
</style>
